import { useCallback } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { Screen } from '../components/Screen'
import { EmptyState, ErrorState, Skeleton } from '../components/States'
import { useAiAction, useAiActionDecision, useClubContext } from '../data/hooks'
import { STATUS_LABEL, aiActionExplanation, aiActionLink } from '../data/aiAction'
import { SliceApiError, type AiAction, type AiActionStatus } from '../data/types'

/**
 * S14 «Проверить действие» (SC08). Штаб подготовил массовое изменение или
 * удаление - здесь его точный список: кто и как изменится, а у удаления ещё
 * и что пропадёт, что останется и можно ли вернуть. Ничего не меняется, пока не нажато
 * «Подтвердить»; повторное нажатие (и подтверждение в боте) не исполняет
 * действие второй раз - сервер отдаёт сохранённый итог.
 */

function badgeClass(status: AiActionStatus): string {
  if (status === 'executed') return 'pc-badge pc-badge--success'
  if (status === 'failed') return 'pc-badge pc-badge--danger'
  if (status === 'pending' || status === 'executing') return 'pc-badge'
  return 'pc-badge pc-badge--neutral'
}

export function AiActionBadge({ status }: { status: AiActionStatus }) {
  return <span className={badgeClass(status)}>{STATUS_LABEL[status]}</span>
}

function Changes({ items }: { items: AiAction['items'] }) {
  return (
    <ul className="pc-list pc-changes" aria-label="Что изменится">
      {items.map((it, i) => (
        <li key={`${it.label}-${i}`} className="pc-change">
          <span className="pc-change__who">{it.label}</span>
          <span className="pc-change__what">
            {it.before}
            <span aria-hidden="true"> → </span>
            <span className="pc-visually-hidden"> станет </span>
            {it.after}
          </span>
        </li>
      ))}
    </ul>
  )
}

/** Пояснение к списку: кого не меняем; у удаления - что скроется и можно ли вернуть. */
function Note({ text, danger }: { text: string; danger?: boolean }) {
  return (
    <div className={danger ? 'pc-aa-note pc-aa-note--danger' : 'pc-aa-note'}>
      {text.split('\n').map((line, i) => (
        <p key={i}>{line}</p>
      ))}
    </div>
  )
}

function Result({ action }: { action: AiAction }) {
  const r = action.result
  if (!r || (r.done.length === 0 && r.failed.length === 0)) return null
  const staleTitle = action.status === 'failed' && action.errorCode === 'stale'
  return (
    <>
      {r.done.length > 0 && (
        <section className="pc-section" aria-labelledby="pc-aa-done">
          <h2 className="pc-section-title" id="pc-aa-done">
            Сделано
          </h2>
          {r.summary && action.status === 'executed' && <p className="pc-muted">{r.summary}</p>}
          <ul className="pc-list pc-changes">
            {r.done.map((it, i) => (
              <li key={`${it.label}-${i}`} className="pc-change">
                <span className="pc-change__who">{it.label}</span>
                {it.detail && <span className="pc-change__what">{it.detail}</span>}
              </li>
            ))}
          </ul>
        </section>
      )}
      {r.failed.length > 0 && (
        <section className="pc-section" aria-labelledby="pc-aa-failed">
          <h2 className="pc-section-title" id="pc-aa-failed">
            {staleTitle ? 'Изменилось после подготовки' : 'Не сделано'}
          </h2>
          <ul className="pc-list pc-changes">
            {r.failed.map((it, i) => (
              <li key={`${it.label}-${i}`} className="pc-change">
                <span className="pc-change__who">{it.label}</span>
                {it.detail && <span className="pc-change__what">{it.detail}</span>}
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  )
}

export function AiActionScreen() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const action = useAiAction(id)
  const decision = useAiActionDecision(id)
  const club = useClubContext()

  const back = useCallback(() => {
    if (location.key !== 'default') navigate(-1)
    else navigate('/hub', { replace: true })
  }, [location.key, navigate])

  const a = action.data
  const busy = decision.phase === 'confirming' || decision.phase === 'cancelling'

  let body
  if (action.isPending) {
    body = <Skeleton rows={3} label="Загружаю действие" />
  } else if (action.isError || !a) {
    const notFound = action.error instanceof SliceApiError && action.error.kind === 'not_found'
    body = notFound ? (
      <EmptyState
        title="Действие недоступно"
        text="Его нет, оно не ваше или у вас больше нет доступа к этому клубу. Ничего не изменено."
        action={
          <Link className="pc-btn" to="/hub">
            Вернуться в Штаб
          </Link>
        }
      />
    ) : (
      <ErrorState title="Не удалось загрузить действие" onRetry={() => void action.refetch()} />
    )
  } else {
    // Ссылка на объект - чтобы проверить итог; если ничего не менялось, проверять нечего
    const link = a.status === 'executed' || a.status === 'failed' ? aiActionLink(a) : null
    const decidedHere = !!decision.decision && !decision.decision.replayed
    body = (
      <>
        <div className="pc-aa-head">
          <AiActionBadge status={a.status} />
          <p className="pc-aa-explain" role="status">
            {aiActionExplanation(a, { decidedHere, replayed: decision.decision?.replayed })}
          </p>
        </div>

        <section className="pc-section pc-card" aria-labelledby="pc-aa-title">
          <h2 className="pc-section-title" id="pc-aa-title">
            {a.title}
          </h2>
          {a.summary && <p className="pc-muted">{a.summary}</p>}
          <Changes items={a.items} />
          {a.note && <Note text={a.note} danger={a.danger} />}
          {club.data && (
            <p className="pc-aa-where pc-muted">
              {club.data.clubName} / {club.data.directionName}
            </p>
          )}
        </section>

        <Result action={a} />

        {link && (
          <Link className="pc-btn pc-btn--block pc-aa-link" to={link.to}>
            {link.label}
          </Link>
        )}

        {decision.phase === 'failed' && (
          <div className="pc-alert" role="alert">
            <span style={{ flex: 1 }}>
              {decision.failedKind === 'cancel'
                ? 'Не удалось отменить: нет связи с сервером. Повторить безопасно.'
                : 'Не удалось отправить подтверждение: нет связи с сервером. Повторить безопасно - дважды действие не выполнится.'}
            </span>
          </div>
        )}
      </>
    )
  }

  const actionBar =
    a && a.status === 'pending' ? (
      <>
        <button
          type="button"
          className={a.danger ? 'pc-btn pc-btn--danger pc-btn--block' : 'pc-btn pc-btn--primary pc-btn--block'}
          disabled={busy}
          onClick={() => void decision.decide('confirm')}
        >
          {decision.phase === 'confirming' ? 'Выполняю' : a.confirmLabel}
        </button>
        <button type="button" className="pc-btn" disabled={busy} onClick={() => void decision.decide('cancel')}>
          {decision.phase === 'cancelling' ? 'Отменяю' : 'Отмена'}
        </button>
      </>
    ) : undefined

  return (
    <Screen title="Проверить действие" back={back} hideHub actionBar={actionBar}>
      {body}
    </Screen>
  )
}

/**
 * Карточка действия под ответом Штаба: что подготовлено, в каком оно
 * состоянии и вход в S14. Сама ничего не подтверждает: подтверждать можно
 * только после просмотра точного списка.
 */
export function AiActionCard({ id }: { id: string }) {
  const navigate = useNavigate()
  const action = useAiAction(id)

  if (action.isPending) {
    return (
      <div className="pc-aa-card" role="status">
        <span className="pc-muted">Загружаю действие</span>
      </div>
    )
  }
  if (action.isError || !action.data) {
    const notFound = action.error instanceof SliceApiError && action.error.kind === 'not_found'
    return (
      <div className="pc-aa-card">
        <span className="pc-muted">{notFound ? 'Действие недоступно.' : 'Действие не загрузилось.'}</span>
        {!notFound && (
          <button type="button" className="pc-btn" onClick={() => void action.refetch()}>
            Повторить
          </button>
        )}
      </div>
    )
  }

  const a = action.data
  return (
    <div className="pc-aa-card">
      <AiActionBadge status={a.status} />
      <p className="pc-aa-card__title">{a.title}</p>
      {a.summary && <p className="pc-muted">{a.summary}</p>}
      <button
        type="button"
        className={a.status === 'pending' ? 'pc-btn pc-btn--primary pc-btn--block' : 'pc-btn pc-btn--block'}
        onClick={() => navigate(`/hub/actions/${a.id}`)}
      >
        {a.status === 'pending' ? 'Проверить и подтвердить' : 'Открыть'}
      </button>
    </div>
  )
}
